import React, { useState } from "react";
import EyeIcon from "./EyeIcon";
import { apiUrl } from "../utils/api";

export default function ChangePasswordModal({ isOpen, onClose, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newPassword.trim()) {
      setError("New password is required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (!currentPassword) {
      setError("Current password is required.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl("/api/v1/auth/change_password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          previous_password: currentPassword,
          proposed_password: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || "Could not update password. Please try again.");
        setLoading(false);
        return;
      }
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || "Could not update password. Please try again.");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setError(null);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 id="change-password-title" className="text-xl font-semibold text-slate-800">
              Change password
            </h2>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md animate-notice-slide-in">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <label htmlFor="change-current-password" className="block text-sm font-medium text-slate-700 mb-1">
              Current password
            </label>
            <div className="relative mb-4">
              <input
                id="change-current-password"
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:border-[#2B7F8D]"
                placeholder="Enter current password"
                autoComplete="current-password"
                aria-label="Current password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-[#2B7F8D]"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label={showCurrent ? "Hide password" : "Show password"}
              >
                <EyeIcon open={showCurrent} />
              </button>
            </div>

            <label htmlFor="change-new-password" className="block text-sm font-medium text-slate-700 mb-1">
              New password
            </label>
            <div className="relative mb-4">
              <input
                id="change-new-password"
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:border-[#2B7F8D]"
                placeholder="Enter new password"
                autoComplete="new-password"
                aria-label="New password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-[#2B7F8D]"
                onClick={() => setShowNew((v) => !v)}
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                <EyeIcon open={showNew} />
              </button>
            </div>

            <label htmlFor="change-confirm-password" className="block text-sm font-medium text-slate-700 mb-1">
              Confirm new password
            </label>
            <input
              id="change-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:border-[#2B7F8D] mb-6"
              placeholder="Confirm new password"
              autoComplete="new-password"
              aria-label="Confirm new password"
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                className="flex-1 px-4 py-2 text-sm bg-[#2B7F8D] text-white rounded-md hover:bg-[#256f7a] focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
